/**
 * OAuthPopupService — opens an OAuth popup, handles PKCE exchange,
 * and returns tokens. Used on web only (Android uses native bridge).
 *
 * Handles two return paths:
 * 1. Desktop: popup posts tokens via postMessage
 * 2. Mobile browser: popup writes to sessionStorage, we poll
 */

import { generatePKCE } from './pkce'
import { OAuthProviderConfig, StoredTokens, isTokenExpired } from './types'
import { cognitoProvider } from './CognitoProvider'

const SK_CODE_VERIFIER = 'jwo-oauth-code-verifier'
const SK_STATE = 'jwo-oauth-state'
const SK_TOKENS = 'jwo-oauth-tokens'
const SK_PROVIDER_CONFIG = 'jwo-oauth-provider-config'
const SK_DOMAIN = 'jwo-oauth-domain'
export const POPUP_MSG_TYPE = 'jwo-oauth-callback'

function sessionSet (key: string, value: unknown): void {
    sessionStorage.setItem(key, JSON.stringify(value))
}

function sessionGet<T> (key: string): T | null {
    try {
        const raw = sessionStorage.getItem(key)
        return raw ? JSON.parse(raw) : null
    } catch {
        return null
    }
}

function getRedirectUri (): string {
    const uri = window.location.origin + window.location.pathname
    return uri.endsWith('/') ? uri.slice(0, -1) : uri
}

/**
 * Authenticate via OAuth popup. Returns tokens on success.
 */
export async function authenticate (config: OAuthProviderConfig, domain: string): Promise<StoredTokens> {
    sessionSet(SK_DOMAIN, domain)
    sessionSet(SK_PROVIDER_CONFIG, config)

    const { codeVerifier, codeChallenge } = await generatePKCE()
    sessionSet(SK_CODE_VERIFIER, codeVerifier)

    const state = crypto.randomUUID()
    sessionSet(SK_STATE, state)

    const authUrl = cognitoProvider.buildAuthUrl(config, codeChallenge, getRedirectUri(), state)

    const w = 500
    const h = 650
    const left = window.screenX + (window.outerWidth - w) / 2
    const top = window.screenY + (window.outerHeight - h) / 2
    const popup = window.open(
        authUrl,
        'oauth-login',
        `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`,
    )

    if (!popup) {
        throw new Error('Popup blocked — please allow popups for this site')
    }

    return waitForTokens(popup)
}

/**
 * Handle OAuth callback in the popup window (called from platform entry point).
 * Returns true if this page is a callback and was handled.
 */
export async function handleOAuthCallback (): Promise<boolean> {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const returnedState = params.get('state')
    if (!code || !returnedState) return false

    const savedState = sessionGet<string>(SK_STATE)
    const savedConfig = sessionGet<OAuthProviderConfig>(SK_PROVIDER_CONFIG)
    const savedVerifier = sessionGet<string>(SK_CODE_VERIFIER)

    if (savedState !== returnedState || !savedConfig || !savedVerifier) {
        sessionStorage.removeItem(SK_STATE)
        sessionStorage.removeItem(SK_CODE_VERIFIER)
        sessionStorage.removeItem(SK_PROVIDER_CONFIG)
        window.history.replaceState({}, '', window.location.pathname)
        return false
    }

    try {
        const tokens = await cognitoProvider.exchangeCodeForTokens(
            savedConfig, code, savedVerifier, getRedirectUri(),
        )
        sessionStorage.setItem(SK_TOKENS, JSON.stringify(tokens))
        sessionStorage.removeItem(SK_CODE_VERIFIER)
        sessionStorage.removeItem(SK_STATE)

        if (window.opener) {
            window.opener.postMessage({
                type: POPUP_MSG_TYPE,
                tokens,
                providerConfig: savedConfig,
                domain: sessionGet<string>(SK_DOMAIN),
            }, window.location.origin)
            window.close()
            return true
        }

        // Mobile: tokens in sessionStorage, polling will pick them up
        window.close()
        return true
    } catch (err) {
        if (window.opener) {
            window.opener.postMessage({
                type: POPUP_MSG_TYPE,
                error: (err as Error).message || 'Token exchange failed',
            }, window.location.origin)
            window.close()
        }
        return true
    }
}

/**
 * Restore tokens from sessionStorage (session restore on page reload).
 * An expired access token is still restored when a refresh token is saved with it, so the session can be
 * refreshed instead of sending the user back to sign in; the caller is responsible for that refresh. It also needs
 * its expiresAt: without one, the token store would date the restored token from now and treat it as valid for a
 * fresh lifetime.
 */
export function restoreSession (): StoredTokens | null {
    const tokens = sessionGet<StoredTokens>(SK_TOKENS)
    if (tokens && (!isTokenExpired(tokens) || (tokens.refreshToken && tokens.expiresAt))) return tokens
    return null
}

/**
 * Persist tokens to sessionStorage in the main window.
 * Called after popup auth completes — sessionStorage is per-window,
 * so the main window must store its own copy.
 */
export function persistSession (tokens: StoredTokens, config: OAuthProviderConfig, domain: string): void {
    sessionSet(SK_TOKENS, tokens)
    sessionSet(SK_PROVIDER_CONFIG, config)
    sessionSet(SK_DOMAIN, domain)
}

/**
 * Overwrite the saved tokens after a token refresh, so a page reload restores the current tokens
 * rather than the ones issued at sign-in (which by then may be expired or close to it).
 * No-op when there is no saved session — e.g. after sign-out — so a refresh that resolves after
 * sign-out cannot bring the session back. Storage errors are swallowed: failing to persist must not
 * fail the refresh itself.
 */
export function updateSessionTokens (tokens: StoredTokens): void {
    try {
        if (!sessionGet<StoredTokens>(SK_TOKENS)) return
        sessionSet(SK_TOKENS, tokens)
    } catch (error) {
        console.warn('[OAuthPopupService] Could not persist refreshed tokens', error)
    }
}

export function getStoredProviderConfig (): OAuthProviderConfig | null {
    return sessionGet<OAuthProviderConfig>(SK_PROVIDER_CONFIG)
}

export function getStoredDomain (): string | null {
    return sessionGet<string>(SK_DOMAIN)
}

export function clearSession (): void {
    sessionStorage.removeItem(SK_TOKENS)
    sessionStorage.removeItem(SK_CODE_VERIFIER)
    sessionStorage.removeItem(SK_STATE)
    sessionStorage.removeItem(SK_PROVIDER_CONFIG)
    sessionStorage.removeItem(SK_DOMAIN)
}

/**
 * Build the Cognito Hosted UI /logout URL for a top-level sign-out redirect, or
 * return null when the provider config lacks the fields needed to construct it.
 *
 * A top-level navigation to this URL clears the Cognito session cookie as a
 * FIRST-PARTY request (the browser is on the Cognito domain during the redirect),
 * so it works in every browser. A hidden iframe cannot: its request to the
 * Cognito domain is third-party and is blocked by Safari ITP / Chrome's
 * third-party-cookie phase-out, leaving the cookie — and the shared-desktop gap
 * where the previous user is silently re-authenticated — intact (P494835427).
 *
 * `logout_uri` must exactly match a registered sign-out URL on the app client;
 * we use the bare app origin, which is registered (CognitoHandler registers
 * callbackURLs and logoutURLs as the same list, and login already redirects to
 * this origin). On return the app boots unauthenticated and renders WebLoginPage,
 * which needs no ?domain/region/storeId params — so the paramless landing is the
 * intended logged-out login screen, not the "A domain is required" provision error.
 */
export function buildCognitoLogoutUrl (config: OAuthProviderConfig): string | null {
    const meta = config.providerMetadata || {}
    const cognitoDomain = meta.cognitoDomain
    const region = meta.region
    if (!cognitoDomain || !region || !config.clientId) return null

    return (
        `https://${cognitoDomain}.auth.${region}.amazoncognito.com/logout` +
        `?client_id=${encodeURIComponent(config.clientId)}` +
        `&logout_uri=${encodeURIComponent(window.location.origin)}`
    )
}

// ---------------------------------------------------------------------------
// Internal: wait for popup to return tokens
// ---------------------------------------------------------------------------

const POPUP_TIMEOUT_MS = 5 * 60 * 1000

function waitForTokens (popup: Window): Promise<StoredTokens> {
    return new Promise((resolve, reject) => {
        let handled = false

        function onMessage (event: MessageEvent) {
            if (handled) return
            if (event.origin !== window.location.origin) return
            if (event.data?.type !== POPUP_MSG_TYPE) return

            handled = true
            cleanup()

            if (event.data.error) {
                sessionStorage.removeItem(SK_CODE_VERIFIER)
                sessionStorage.removeItem(SK_STATE)
                reject(new Error(event.data.error))
                return
            }

            resolve(event.data.tokens as StoredTokens)
        }

        const timeout = setTimeout(() => {
            if (handled) return
            handled = true
            cleanup()
            try { popup.close() } catch { /* best-effort */ }
            reject(new Error('OAuth popup timed out'))
        }, POPUP_TIMEOUT_MS)

        // Fallback: poll sessionStorage (mobile browser)
        const interval = setInterval(() => {
            if (handled) { clearInterval(interval); return }
            if (popup.closed) {
                const tokens = sessionGet<StoredTokens>(SK_TOKENS)
                if (tokens && !isTokenExpired(tokens)) {
                    handled = true
                    cleanup()
                    resolve(tokens)
                } else {
                    handled = true
                    cleanup()
                    reject(new Error('Popup closed without completing authentication'))
                }
                return
            }

            const saved = sessionGet<StoredTokens>(SK_TOKENS)
            if (saved && !isTokenExpired(saved)) {
                handled = true
                cleanup()
                resolve(saved)
            }
        }, 1000)

        function cleanup () {
            window.removeEventListener('message', onMessage)
            clearInterval(interval)
            clearTimeout(timeout)
        }

        window.addEventListener('message', onMessage)
    })
}
